import { queryKeys } from './useQueryFavourite'
import { useFetchFavourite } from './useFetchFavourite'
import { useQueryClientUtils } from '../../useQueryClientUtils'

type GetAllUnpaginatedType = ReturnType<ReturnType<typeof useFetchFavourite>['getAllUnpaginated']>

const add = () => {
  const { add } = useFetchFavourite()
  const queryClient = useQueryClient()
  const { cancelQueries, invalidateQueries } = useQueryClientUtils()

  return useMutation({
    mutationFn: add,
    onMutate: ({ workspaceId }) => {
      cancelQueries([queryKeys.allUnpaginated({ workspaceId }).queryKey, queryKeys.all({ workspaceId }).queryKey])
    },
    onSuccess: (responseData, { workspaceId }) => {
      if (!responseData.data || !responseData.included)
        return

      queryClient.setQueryData<Awaited<GetAllUnpaginatedType>>(
        queryKeys.allUnpaginated({ workspaceId }).queryKey,
        oldData => ({
          ...oldData,
          data: oldData?.data ? [...oldData.data, responseData.data] : [responseData.data],
          included: oldData?.included ? [...oldData.included, ...(responseData.included ?? [])] : responseData.included,
        }),
      )

      invalidateQueries([queryKeys.all({ workspaceId }).queryKey])
    },
    onError: (_, { workspaceId }) =>
      queryClient.invalidateQueries({ ...queryKeys.allUnpaginated({ workspaceId }) }),
  })
}

const remove = () => {
  const { remove } = useFetchFavourite()
  const { invalidateQueries, cancelQueries } = useQueryClientUtils()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: remove,
    onMutate: ({ workspaceId }) => {
      cancelQueries([queryKeys.allUnpaginated({ workspaceId }).queryKey, queryKeys.all({ workspaceId }).queryKey])
    },
    onSuccess: (_, { id: favouriteId, workspaceId }) => {
      queryClient.setQueryData<Awaited<GetAllUnpaginatedType>>(
        queryKeys.allUnpaginated({ workspaceId }).queryKey,
        (oldData) => {
          if (!oldData || !oldData.included)
            return undefined

          const favouriteTemplateId = oldData.data.find(({ id }) => id === favouriteId)
            ?.relationships
            ?.template
            ?.data
            .id

          return {
            ...oldData,
            data: oldData.data.filter(favourite => favourite.id !== favouriteId),
            included: oldData.included.filter(template => template.id !== favouriteTemplateId),
          }
        },
      )

      invalidateQueries([queryKeys.all({ workspaceId }).queryKey])
    },
    onError: (_, { workspaceId }) => {
      invalidateQueries([queryKeys.allUnpaginated({ workspaceId }).queryKey, queryKeys.all({ workspaceId }).queryKey])
    },
  })
}

export const useMutationFavourite = {
  add,
  remove,
}
